import React, { type ComponentType } from 'react';
import { Navigate, Route, useParams } from 'react-router-dom';
import { NotFoundPage } from '@/components/NotFoundPage';
import { lazyPage } from '@/lib/lazy-page';

// Pages load on demand, so the first visit downloads the shell and the page it lands on,
// not every page. Layouts show a loading state while a page's code arrives.
const named = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazyPage(() => load().then((module) => ({ default: module[name] })));

const ProjectLayout = lazyPage(() => import('@/pages/project/ProjectLayout'));
const TasksPage = lazyPage(() => import('@/pages/tasks'));
const MasterBoardPage = lazyPage(() => import('@/pages/master-board'));
const InboxPage = lazyPage(() => import('@/pages/inbox'));
const ProjectsPage = named(() => import('@/pages/factory/ProjectsPage'), 'ProjectsPage');
const ProjectDashboardPage = named(() => import('@/pages/factory/dashboard/ProjectDashboardPage'), 'ProjectDashboardPage');
const ProjectRunsPage = named(() => import('@/pages/factory/ProjectRunsPage'), 'ProjectRunsPage');
const RunDetailPage = named(() => import('@/pages/factory/RunDetailPage'), 'RunDetailPage');
const ProjectSettingsPage = named(() => import('@/pages/factory/ProjectSettingsPage'), 'ProjectSettingsPage');
const AllRunsPage = named(() => import('@/pages/factory/AllRunsPage'), 'AllRunsPage');
const UsagePage = named(() => import('@/pages/factory/UsagePage'), 'UsagePage');
const TimeCalendarPage = named(() => import('@/pages/factory/TimeCalendarPage'), 'TimeCalendarPage');
const FleetPage = named(() => import('@/pages/factory/FleetPage'), 'FleetPage');
const MachineDetailPage = named(() => import('@/pages/factory/fleet/MachineDetailPage'), 'MachineDetailPage');
const ChatLayout = named(() => import('@/pages/chat/ChatLayout'), 'ChatLayout');
const NewChatPage = named(() => import('@/pages/chat/NewChatPage'), 'NewChatPage');
const ChatSessionPage = named(() => import('@/pages/chat/ChatSessionPage'), 'ChatSessionPage');
const AiPage = named(() => import('@/pages/factory/AiPage'), 'AiPage');
const AgentConfigPage = named(() => import('@/pages/factory/AgentConfigPage'), 'AgentConfigPage');
const GuardCatchesPage = named(() => import('@/pages/factory/GuardCatchesPage'), 'GuardCatchesPage');
const PeoplePage = named(() => import('@/pages/factory/PeoplePage'), 'PeoplePage');
const ApiKeysPage = named(() => import('@/pages/factory/ApiKeysPage'), 'ApiKeysPage');
// The component gallery: local dev only.
const DevUiPage = import.meta.env.DEV ? lazyPage(() => import('@/pages/dev-ui')) : null;

/** `/p/:slug` opens the project's Board, its home (SF-T-189). */
function ProjectIndexRedirect() {
  const { slug = '' } = useParams<{ slug: string }>();
  return <Navigate to={`/p/${encodeURIComponent(slug)}/board`} replace />;
}

/**
 * The signed-in pages, as `<Route>`s: the app's router puts them inside the signed-in shell, and a
 * link dialog (SF-T-160, pages/link-dialog) inside its own router, with its own `project` layout
 * (it mustn't change the page underneath's project) and `projectBoard` (a task on its own, not the
 * Board with the task over it).
 */
export function pageRoutes(overrides: { project?: ComponentType; projectBoard?: ComponentType } = {}) {
  const Project = overrides.project ?? ProjectLayout;
  const ProjectBoard = overrides.projectBoard ?? TasksPage;
  return (
    <>
      {/* Home: every project's tasks on one Board (T-26). A task opens over it at /board/<task id>. */}
      <Route path="/" element={<Navigate to="/board" replace />} />
      <Route path="/board/:taskId?" element={<MasterBoardPage />} />
      {/* What's waiting on me across every project (T-37). A task opens over it at /inbox/<task id>. */}
      <Route path="/inbox/:taskId?" element={<InboxPage />} />
      {/* Chat with an agent on a fleet machine, across every project (admins, T-51): the sessions
          beside New chat (/chat; /chat/new on a phone, where /chat is the list) or a chat (T-54). */}
      <Route path="/chat" element={<ChatLayout />}>
        <Route index element={<NewChatPage />} />
        <Route path="new" element={<NewChatPage />} />
        <Route path=":sessionId" element={<ChatSessionPage />} />
      </Route>
      <Route path="/projects" element={<ProjectsPage />} />

      <Route path="/p/:slug" element={<Project />}>
        <Route index element={<ProjectIndexRedirect />} />
        <Route path="overview" element={<ProjectDashboardPage />} />
        {/* One route, so opening a task over the Board keeps the Board (and its scroll) mounted.
            A task's URL is /p/<slug>/board/<task id>, as the API's task links are. */}
        <Route path="board/:taskId?" element={<ProjectBoard />} />
        <Route path="runs" element={<ProjectRunsPage />} />
        <Route path="runs/:runNumber" element={<RunDetailPage />} />
        <Route path="settings" element={<ProjectSettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="/runs" element={<AllRunsPage />} />
      <Route path="/usage" element={<UsagePage />} />
      <Route path="/usage/calendar" element={<TimeCalendarPage />} />
      {/* Admins only; anyone else sees "Only admins" there (the sidebar hides them). */}
      <Route path="/fleet" element={<FleetPage />} />
      <Route path="/fleet/:machineId" element={<MachineDetailPage />} />
      <Route path="/ai" element={<AiPage />} />
      <Route path="/agent-config" element={<AgentConfigPage />} />
      <Route path="/guard-catches" element={<GuardCatchesPage />} />
      <Route path="/admin/people" element={<PeoplePage />} />
      <Route path="/settings/api-keys" element={<ApiKeysPage />} />

      {DevUiPage ? <Route path="/dev/ui" element={<DevUiPage />} /> : null}

      {/* Catch-all: "Page not found" inside the app; signed-out visitors go to
          /login first and come back here. */}
      <Route path="*" element={<NotFoundPage />} />
    </>
  );
}
